import { inject, observer } from "mobx-react";
import React, { useCallback } from "react";
import BroadcastStreamStore from "common/broadcast/_stores/broadcast-stream/BroadcastStreamStore";
import { BroadcastStreamType } from "common/broadcast/_stores/broadcast-stream/enums";
import TailwindButton from "library/components/_tailwind/button";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import LanguageStore from "library/core/stores/language/LanguageStore";
import LayoutStore from "library/core/stores/layout/LayoutStore";
import TailwindTooltip from "library/components/_tailwind/tooltip";

type Props = {
  broadcastStreamStore?: BroadcastStreamStore;
  broadcastStore?: BroadcastStore;
  languageStore?: LanguageStore;
  layoutStore?: LayoutStore;
};

const BroadcastStreamTypeSwitcher: React.FunctionComponent<Props> = ({
  broadcastStreamStore,
  broadcastStore,
  languageStore,
  layoutStore,
  //intl,
}) => {
  const {
    streamType,
    setStreamType,
    mediaStream,
    stopWebRTCBroadcast,
    obsStreamConnected,
  } = broadcastStreamStore!;

  const { intl } = languageStore!;
  const isFirefox = layoutStore?.isFirefox;

  const { isShowStarted } = broadcastStore!;
  const onClickChangeStreamType = useCallback(async () => {
    if (mediaStream) {
      await stopWebRTCBroadcast();
    }

    await setStreamType(
      streamType === BroadcastStreamType.VR && obsStreamConnected
        ? BroadcastStreamType.OBS
        : streamType === BroadcastStreamType.VR
        ? null
        : streamType === BroadcastStreamType.WEBRTC
        ? BroadcastStreamType.OBS
        : BroadcastStreamType.WEBRTC
    );
  }, [streamType, stopWebRTCBroadcast, mediaStream]);

  if (!streamType || isShowStarted) {
    return null;
  }

  // Don't show the switcher button if in OBS mode and Firefox is detected
  if (streamType === BroadcastStreamType.OBS && isFirefox) {
    return null;
  }

  const button = (
    <TailwindButton
      data-testid={"stream-type-switcher"}
      data-current-stream-type={streamType}
      className={[`BroadcastPreview__streamTypeSwitcher`]}
      onClick={onClickChangeStreamType}
      size={"sm"}
      fullWidth={false}
      position={"absolute"}
      inset={["bottom-2", "left-2"]}
      backgroundColor={"primary"}
      disabled={streamType === BroadcastStreamType.OBS && isFirefox}>
      {streamType === BroadcastStreamType.VR
        ? intl.formatMessage({
            id: "common.back",
            defaultMessage: "Back",
          })
        : streamType === BroadcastStreamType.WEBRTC
        ? intl.formatMessage({
            id: "broadcast.obs-instead",
            defaultMessage: "Use OBS instead",
          })
        : intl.formatMessage({
            id: "broadcast.webcam-instead",
          })}
    </TailwindButton>
  );

  // Add tooltip if in OBS mode and Firefox is detected
  if (streamType === BroadcastStreamType.OBS && isFirefox) {
    return (
      <TailwindTooltip
        content={intl.formatMessage({
          id: "broadcast.broadcastPreview.firefox-not-supported",
          defaultMessage: "Firefox not supported. Please use Chrome or another modern browser for the best experience.",
        })}
        type="error">
        {button}
      </TailwindTooltip>
    );
  }

  return button;
};

export default inject(
  "broadcastStreamStore",
  "broadcastStore",
  "languageStore",
  "layoutStore"
)(observer(BroadcastStreamTypeSwitcher));
